import { FC, useEffect, useId, useMemo, useState } from 'react';
import { Button, Typography } from 'antd';
import { capitalCase } from 'change-case';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ReferenceArea,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { AppIcon } from '@/assets';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { CustomRechartsTooltip, useContextualCommentTarget } from '@/shared';
import FinancialCompanyAvatar from '@/shared/components/financial-statements/components/FinancialCompanyAvatar';
import { formatFinancialMetricWithDisplay, useFinancialValueDisplay } from '@/shared/components/financial-values';
import { SetStateMethod } from '@/types';
import { dayjs, formatFinancialPeriodLabel, FundamentalValuationsType } from '@/utilities';

import { createMetricChartSeries, mergeMetricChartData, metricChartSeriesVisibilityKey } from '../metricComparison';
import type { MetricChartCompany, MetricChartComparisonDataset, MetricChartDataRow } from '../metricComparison';

import './index.css';

interface MetricChartRangeChange {
    changePercent: number | null;
    endValue: number | null;
    lineItem: string;
    startValue: number | null;
}

interface MetricChartTickProps {
    payload?: { value?: number | string };
    x?: number | string;
    y?: number | string;
}

interface MetricChartProps {
    company?: MetricChartCompany;
    comparisonCompanies?: MetricChartCompany[];
    comparisonData?: MetricChartComparisonDataset[];
    aspect?: number;
    chartedLineItem: string;
    tags: string[];
    data: MetricChartDataRow[];
    hiddenSeriesKeys?: string[];
    lineItemLabels?: Partial<Record<string, string>>;
    segmentedValue: string;
    setTags?: SetStateMethod<string[]>;
    setSegmentedValue: SetStateMethod<string>;
    title?: string;
    type?: FundamentalValuationsType;
    typeByLineItem?: Partial<Record<string, FundamentalValuationsType>>;
}

const MetricChart: FC<MetricChartProps> = ({
    company,
    comparisonCompanies = [],
    comparisonData = [],
    data,
    hiddenSeriesKeys = [],
    lineItemLabels,
    tags,
    aspect,
    type,
    typeByLineItem,
    segmentedValue,
    setTags,
    title = 'Financial Series',
}): JSX.Element => {
    const gradientRootId = useId().replace(/:/g, '');
    const { preference: financialValueDisplay } = useFinancialValueDisplay();
    const contextualCommentTarget = useContextualCommentTarget();
    const [selectionStart, setSelectionStart] = useState<number | string>();
    const [selectionEnd, setSelectionEnd] = useState<number | string>();
    const targetSelection = contextualCommentTarget?.target.metadata?.selection as Record<string, unknown> | undefined;

    useEffect(() => {
        if (targetSelection?.kind !== 'chart-range') {
            return;
        }

        const { lineItems } = targetSelection;

        if (Array.isArray(lineItems)) {
            const restoredLineItems = lineItems.filter(
                (lineItem): lineItem is string => typeof lineItem === 'string' && Boolean(lineItem.trim()),
            );

            if (
                restoredLineItems.length &&
                (restoredLineItems.length !== tags.length ||
                    restoredLineItems.some((lineItem, index) => lineItem !== tags[index]))
            ) {
                queueMicrotask(() => setTags?.(restoredLineItems));
            }
        }
    }, [setTags, tags, targetSelection]);

    const restoredSelectionStart =
        typeof targetSelection?.start === 'string' || typeof targetSelection?.start === 'number'
            ? targetSelection.start
            : undefined;
    const restoredSelectionEnd =
        typeof targetSelection?.end === 'string' || typeof targetSelection?.end === 'number'
            ? targetSelection.end
            : undefined;
    const displaySelectionStart = selectionStart === undefined ? restoredSelectionStart : selectionStart;
    const displaySelectionEnd = selectionStart === undefined ? restoredSelectionEnd : selectionEnd;

    const mergedData = useMemo(() => mergeMetricChartData(data, comparisonData, tags), [comparisonData, data, tags]);
    const allSeries = useMemo(
        () => createMetricChartSeries(tags, company, comparisonCompanies),
        [company, comparisonCompanies, tags],
    );
    const hiddenSeriesKeySet = useMemo(() => new Set(hiddenSeriesKeys), [hiddenSeriesKeys]);
    const series = useMemo(
        () =>
            allSeries.filter(
                (item) => !hiddenSeriesKeySet.has(metricChartSeriesVisibilityKey(item.company.symbol, item.lineItem)),
            ),
        [allSeries, hiddenSeriesKeySet],
    );
    const visibleLineItems = useMemo(() => [...new Set(series.map((item) => item.lineItem))], [series]);
    const visiblePrimaryLineItems = useMemo(
        () => series.filter((item) => item.companyIndex === 0).map((item) => item.lineItem),
        [series],
    );

    // ? Might not work on some routes if `date` isn't a key on the response
    const sortedData = useMemo(
        () =>
            mergedData.slice().sort((a, b) => {
                return dayjs(a.date).valueOf() - dayjs(b.date).valueOf();
            }) ?? [],
        [mergedData],
    );

    const { token, chartColorPalettes } = useAntDesignTheme();
    const exportCompanies = company ? [company, ...comparisonCompanies] : comparisonCompanies;
    const chartMetadata = `${segmentedValue === 'annual' ? 'Annual' : segmentedValue === 'observations' ? 'Observations' : 'Quarterly'} · ${
        visibleLineItems.length
            ? visibleLineItems.map((tag) => lineItemLabels?.[tag] ?? capitalCase(tag)).join(', ')
            : 'No visible line items'
    }`;
    const colorForSeries = (companyIndex: number, lineItemIndex: number) => {
        const palette = chartColorPalettes[companyIndex % chartColorPalettes.length];

        return palette[lineItemIndex % palette.length];
    };

    const chartValues = useMemo(
        () =>
            sortedData.flatMap(
                (row: Record<string, unknown>) =>
                    series
                        .map((item) => Number(row?.[item.dataKey]))
                        .filter((value) => Number.isFinite(value)) as number[],
            ),
        [series, sortedData],
    );

    const { domain, hasNegativeValues, zeroOffset } = useMemo(() => {
        if (!chartValues.length) {
            return {
                domain: [-1, 1] as [number, number],
                hasNegativeValues: false,
                zeroOffset: 0.5,
            };
        }

        const minValue = Math.min(...chartValues);
        const maxValue = Math.max(...chartValues);
        const hasNegativeValues = minValue < 0;
        const hasPositiveValues = maxValue > 0;
        const span = Math.max(maxValue - minValue, Math.abs(maxValue), Math.abs(minValue), 1);
        const padding = span * 0.08;

        const lowerBound = minValue - padding;
        const upperBound = hasNegativeValues && !hasPositiveValues ? padding : maxValue + padding;
        const domain = [lowerBound, upperBound] as [number, number];

        return {
            domain,
            hasNegativeValues,
            zeroOffset: Math.min(1, Math.max(0, domain[1] / (domain[1] - domain[0]))),
        };
    }, [chartValues]);

    const yAxisTick = ({ x, y, payload }: MetricChartTickProps) => {
        return (
            <text x={x} y={y} dx={8} dy={4} fill={token.colorTextTertiary} fontSize={12} textAnchor='start'>
                {formatFinancialMetricWithDisplay(Number(payload?.value ?? 0), type, financialValueDisplay)}
            </text>
        );
    };

    const rangeSummary = useMemo(() => {
        if (displaySelectionStart === undefined || displaySelectionEnd === undefined) {
            return undefined;
        }

        if (displaySelectionStart === displaySelectionEnd) {
            return undefined;
        }

        const startIndex = sortedData.findIndex((row) => row.date === displaySelectionStart);
        const endIndex = sortedData.findIndex((row) => row.date === displaySelectionEnd);

        if (startIndex < 0 || endIndex < 0 || startIndex === endIndex) {
            return undefined;
        }

        const fromIndex = Math.min(startIndex, endIndex);
        const toIndex = Math.max(startIndex, endIndex);
        const startRow = sortedData[fromIndex];
        const endRow = sortedData[toIndex];
        const rawStart = startRow?.date ?? String(displaySelectionStart);
        const rawEnd = endRow?.date ?? String(displaySelectionEnd);
        const items: MetricChartRangeChange[] = visiblePrimaryLineItems.map((lineItem) => {
            const startValue = Number(startRow?.[lineItem]);
            const endValue = Number(endRow?.[lineItem]);
            const hasComparableValues = Number.isFinite(startValue) && Number.isFinite(endValue) && startValue !== 0;

            return {
                changePercent: hasComparableValues ? ((endValue - startValue) / Math.abs(startValue)) * 100 : null,
                endValue: Number.isFinite(endValue) ? endValue : null,
                lineItem,
                startValue: Number.isFinite(startValue) ? startValue : null,
            };
        });

        return {
            end: rawEnd,
            endLabel: formatFinancialPeriodLabel(rawEnd),
            items,
            start: rawStart,
            startLabel: formatFinancialPeriodLabel(rawStart),
        };
    }, [displaySelectionEnd, displaySelectionStart, sortedData, visiblePrimaryLineItems]);

    const finishSelection = () => {
        if (selectionStart === undefined || selectionEnd === undefined || !contextualCommentTarget) {
            setSelectionStart(undefined);
            setSelectionEnd(undefined);
            return;
        }

        if (!rangeSummary) {
            setSelectionStart(undefined);
            setSelectionEnd(undefined);
            return;
        }

        const baseTarget = contextualCommentTarget.baseTarget;

        contextualCommentTarget.setTarget({
            ...baseTarget,
            metadata: {
                ...(baseTarget.metadata ?? {}),
                selection: {
                    end: rangeSummary.end,
                    endLabel: rangeSummary.endLabel,
                    kind: 'chart-range',
                    lineItemChanges: rangeSummary.items,
                    lineItems: visiblePrimaryLineItems,
                    period: segmentedValue,
                    start: rangeSummary.start,
                    startLabel: rangeSummary.startLabel,
                },
            },
        });
        setSelectionStart(undefined);
        setSelectionEnd(undefined);
    };

    return (
        <div className='metric-chart-export-frame' style={{ background: token.colorBgContainer }}>
            <div className='metric-chart-export-header'>
                <Typography.Title level={3} style={{ margin: 0 }}>
                    {title}
                </Typography.Title>
                <div className='metric-chart-export-description'>
                    <div className='metric-chart-export-companies'>
                        {exportCompanies.map((exportCompany, companyIndex) => {
                            const exportSeries = series.filter((item) => item.companyIndex === companyIndex);

                            if (!exportSeries.length) {
                                return null;
                            }

                            return (
                                <span className='metric-chart-export-company-group' key={exportCompany.symbol}>
                                    <span className='metric-chart-export-company'>
                                        <FinancialCompanyAvatar company={exportCompany} size={20} />
                                        <Typography.Text strong>{exportCompany.companyName}</Typography.Text>
                                        <Typography.Text type='secondary'>{exportCompany.symbol}</Typography.Text>
                                    </span>
                                    <span className='metric-chart-export-series-key'>
                                        {exportSeries.map((item) => {
                                            const color = colorForSeries(companyIndex, item.lineItemIndex);

                                            return (
                                                <span
                                                    className='metric-chart-export-series-key-item'
                                                    key={`${exportCompany.symbol}-${item.lineItem}-export-key`}
                                                >
                                                    <span
                                                        className='metric-chart-export-series-key-swatch'
                                                        style={{
                                                            background: color.fill,
                                                            borderColor: color.stroke,
                                                        }}
                                                    />
                                                    <Typography.Text type='secondary'>
                                                        {lineItemLabels?.[item.lineItem] ?? capitalCase(item.lineItem)}
                                                    </Typography.Text>
                                                </span>
                                            );
                                        })}
                                    </span>
                                </span>
                            );
                        })}
                    </div>
                    <Typography.Text type='secondary'>{chartMetadata}</Typography.Text>
                </div>
            </div>
            <div className='metric-chart-export-canvas'>
                <ResponsiveContainer aspect={aspect ? aspect : undefined} height={'100%'} width={'100%'}>
                    <AreaChart
                        layout='horizontal'
                        data={sortedData}
                        margin={{
                            top: 0,
                            right: 0,
                            left: 0,
                            bottom: 0,
                        }}
                        style={{ backgroundColor: token.colorBgContainer }}
                        onMouseDown={(state) => {
                            if (state.activeLabel === undefined) {
                                return;
                            }

                            contextualCommentTarget?.resetTarget();
                            setSelectionStart(state.activeLabel);
                            setSelectionEnd(state.activeLabel);
                        }}
                        onMouseMove={(state) => {
                            if (selectionStart === undefined || state.activeLabel === undefined) {
                                return;
                            }

                            setSelectionEnd(state.activeLabel);
                        }}
                        onMouseUp={finishSelection}
                        onMouseLeave={() => {
                            if (selectionStart !== undefined) {
                                finishSelection();
                            }
                        }}
                    >
                        <defs>
                            {series.map((item, index) => {
                                const color = colorForSeries(item.companyIndex, item.lineItemIndex);
                                const gradientId = `${gradientRootId}-${index}-${color.name}`;
                                const zeroStop = `${zeroOffset * 100}%`;

                                return (
                                    <linearGradient
                                        key={`${item.dataKey}-${item.companyIndex}-gradient`}
                                        id={gradientId}
                                        x1='0'
                                        y1='0'
                                        x2='0'
                                        y2='1'
                                    >
                                        <stop offset='0%' stopColor={color?.stroke} stopOpacity={0.22} />
                                        <stop offset={zeroStop} stopColor={color?.stroke} stopOpacity={0.08} />
                                        {hasNegativeValues ? (
                                            <>
                                                <stop offset={zeroStop} stopColor={color?.stroke} stopOpacity={0.08} />
                                                <stop offset='100%' stopColor={color?.stroke} stopOpacity={0.04} />
                                            </>
                                        ) : (
                                            <stop offset='100%' stopColor={color?.stroke} stopOpacity={0} />
                                        )}
                                    </linearGradient>
                                );
                            })}
                        </defs>
                        {hasNegativeValues ? (
                            <ReferenceArea
                                y1={domain[0]}
                                y2={0}
                                fill={token.colorFillQuaternary}
                                fillOpacity={0.24}
                                stroke='none'
                            />
                        ) : null}
                        {displaySelectionStart !== undefined && displaySelectionEnd !== undefined ? (
                            <ReferenceArea
                                x1={displaySelectionStart}
                                x2={displaySelectionEnd}
                                fill={token.colorPrimaryBg}
                                fillOpacity={0.5}
                                stroke='none'
                            />
                        ) : null}
                        <CartesianGrid
                            stroke={token.colorBorderSecondary}
                            strokeDasharray='5 5'
                            style={{ borderColor: token.colorBgBase }}
                        />
                        {hasNegativeValues ? (
                            <ReferenceLine
                                y={0}
                                stroke={token.colorBorder}
                                strokeDasharray='4 4'
                                strokeOpacity={0.65}
                                strokeWidth={1.25}
                            />
                        ) : null}

                        <XAxis
                            axisLine={{ stroke: token.colorTextQuaternary }}
                            tick={{ fill: token.colorTextTertiary }}
                            includeHidden
                            allowDataOverflow
                            minTickGap={16}
                            // domain={[`${Number('dataMin') * 0.75}`, `${Number('dataMax') * 1.25}`]}
                            dataKey='date'
                            tickFormatter={(value: string) => {
                                const d = dayjs(value);

                                return segmentedValue === 'annual'
                                    ? d.format('YYYY')
                                    : segmentedValue === 'observations'
                                      ? d.format('MMM D, YYYY')
                                      : `Q${d.quarter()} '${d.format('YY')}`;
                            }}
                            interval={'preserveStart'}
                            height={32}
                            tickMargin={8}
                            style={{ color: token.colorTextSecondary }}
                        />
                        <YAxis
                            axisLine={{ stroke: token.colorTextQuaternary }}
                            tick={yAxisTick}
                            includeHidden
                            allowDataOverflow
                            domain={domain}
                            interval={'preserveStartEnd'}
                            orientation='right'
                            type='number'
                            // domain={[(dataMin: number) => dataMin * 1.25, (dataMax: number) => dataMax * 1.25]}
                            padding={{ top: 0, bottom: 0 }}
                            tickMargin={0}
                            width={144}
                            tickLine={false}
                            style={{ color: token.colorTextSecondary }}
                        />
                        <Tooltip
                            cursor={{ stroke: token.colorBorder }}
                            wrapperStyle={{ pointerEvents: 'auto' }}
                            content={(props) => {
                                return (
                                    <CustomRechartsTooltip
                                        {...props}
                                        data={sortedData}
                                        lineItemLabels={lineItemLabels}
                                        rangeSummary={rangeSummary}
                                        series={series}
                                        type={type}
                                        typeByLineItem={typeByLineItem}
                                        segmentedValue={segmentedValue}
                                    />
                                );
                            }}
                        />
                        {series.map((item, index) => {
                            const color = colorForSeries(item.companyIndex, item.lineItemIndex);
                            const dashPatterns = ['', '5 3', '2 3', '8 3', '10 3 2 3', '1 3', '8 2 2 2', '12 3'];

                            return (
                                <Area
                                    key={`${item.dataKey}-${item.companyIndex}`}
                                    activeDot={{ stroke: token.colorBorder, strokeWidth: 2 }}
                                    animationDuration={300}
                                    baseValue={0}
                                    connectNulls
                                    type='monotone'
                                    dataKey={item.dataKey}
                                    stroke={color.stroke}
                                    strokeDasharray={dashPatterns[item.companyIndex % dashPatterns.length] || undefined}
                                    strokeWidth={2}
                                    fillOpacity={item.companyIndex === 0 ? 1 : 0}
                                    fill={
                                        item.companyIndex === 0
                                            ? `url(#${gradientRootId}-${index}-${color.name})`
                                            : 'transparent'
                                    }
                                />
                            );
                        })}
                        {/* <Customized
                            component={(props: any) => {
                                const { width, height, stroke, fill, formattedGraphicalItems } = props;

                                const firstSeries = formattedGraphicalItems[0];

                                const secondPoint = firstSeries?.props?.points[1];

                                return (
                                    <g>
                                        <foreignObject
                                            x={
                                                secondPoint ===
                                                firstSeries?.props?.points[firstSeries?.props?.points?.length - 1]
                                                    ? secondPoint.x - 36
                                                    : secondPoint.x + 2
                                            }
                                            y={2}
                                            width={50}
                                            height={50}
                                        >
                                            <div style={{ width: '100%', height: '100%' }}>
                                                <Avatar.Group maxCount={3} maxStyle={{ display: 'none' }}>
                                                    <Avatar
                                                        size={16}
                                                        src={user?.avatar}
                                                        style={{ cursor: 'pointer' }}
                                                    />
                                                    <Avatar
                                                        size={16}
                                                        src={user?.avatar}
                                                        style={{ cursor: 'pointer' }}
                                                    />
                                                    <Avatar
                                                        size={16}
                                                        src={user?.avatar}
                                                        style={{ cursor: 'pointer' }}
                                                    />
                                                </Avatar.Group>
                                            </div>
                                        </foreignObject>
                                    </g>
                                );
                            }}
                        /> */}
                    </AreaChart>
                </ResponsiveContainer>
            </div>
            <div className='metric-chart-export-footer'>
                <Button
                    href='https://countercyclical.com'
                    icon={<img alt='' height={18} src={AppIcon} style={{ display: 'block' }} width={18} />}
                    rel='noreferrer'
                    tabIndex={-1}
                    target='_blank'
                >
                    Made with Countercyclical
                </Button>
            </div>
        </div>
    );
};

export default MetricChart;
